import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ExternalLink, Wrench, AlertTriangle, BookOpen, Youtube } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EventName from "@/components/EventName";
import api from "@/lib/api";
import {
    BUILDS_BY_SLUG,
    MANUFACTURER_FALLBACK_RESOURCES,
} from "@/data/builds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BuildDetail — Editorial detail page for a single car or motorcycle.
 *
 * Two render modes:
 *  1. Full editorial — when the entry has `engine` (flagship content)
 *  2. Stub — minimal entry with name + manufacturer fallback resources
 *
 * Image source priority:
 *  1. Admin-uploaded images from backend (curated via /admin/builds)
 *  2. Static images from builds.js (fallback)
 */
export default function BuildDetail() {
    const { slug } = useParams();
    const baseBuild = BUILDS_BY_SLUG[slug];
    const [remoteImages, setRemoteImages] = useState(null); // null = loading, [] = empty

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const { data } = await api.get(`/builds/${slug}/images`);
                if (!cancelled) setRemoteImages(Array.isArray(data) ? data : []);
            } catch (_e) {
                if (!cancelled) setRemoteImages([]);
            }
        }
        if (slug) load();
        return () => { cancelled = true; };
    }, [slug]);

    if (!baseBuild) {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)] flex flex-col">
                <Navbar />
                <section className="flex-1 flex items-center justify-center px-6 md:px-12 py-32">
                    <div className="max-w-xl text-center">
                        <p className="font-body text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-6">
                            Build Not Found
                        </p>
                        <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-6">
                            That Machine Isn&apos;t In The Garage Yet.
                        </h1>
                        <p className="text-[var(--crewz-text-muted)] mb-8">
                            The build you&apos;re looking for hasn&apos;t been catalogued. Return to the
                            collection and pick another icon.
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] px-7 py-4 text-sm tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                            data-testid="build-notfound-home-cta"
                        >
                            <ArrowLeft size={14} />
                            Back To The Collection
                        </Link>
                    </div>
                </section>
                <Footer />
            </div>
        );
    }

    const isFull = Boolean(baseBuild.engine);
    const fallback = MANUFACTURER_FALLBACK_RESOURCES[baseBuild.manufacturer] || null;

    // Merge: if admin uploaded any images for this slug, they take over the gallery.
    // Otherwise fall back to static images from builds.js.
    const mergedImages = (remoteImages && remoteImages.length > 0)
        ? remoteImages.map((r) => ({
            url: r.url.startsWith("http") ? r.url : `${BACKEND_URL}${r.url}`,
            label: r.label || "Build Image",
        }))
        : (baseBuild.images || []);

    const build = { ...baseBuild, images: mergedImages };

    return (
        <div
            className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]"
            data-testid="build-detail-page"
        >
            <Navbar />

            {/* HERO */}
            <section className="relative border-b border-white/5 overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-[var(--crewz-bg-2)] via-[var(--crewz-bg)] to-[var(--crewz-bg-3)]" />
                    {isFull && build.images?.[0] && (
                        <img
                            src={typeof build.images[0] === "string" ? build.images[0] : build.images[0].url}
                            alt={build.fullName || build.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--crewz-bg)] via-[rgba(17,17,17,0.65)] to-[rgba(17,17,17,0.4)]" />
                </div>

                <div className="relative px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-16 md:pb-24 max-w-7xl mx-auto">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--crewz-text-muted)] hover:text-[var(--crewz-gold)] transition-colors mb-8 md:mb-12"
                        data-testid="build-back-link"
                    >
                        <ArrowLeft size={14} />
                        Back
                    </Link>

                    <p className="font-body text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-4">
                        {build.manufacturer} {build.category ? `· ${build.category}` : ""}
                    </p>
                    <h1
                        className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.88] tracking-[-0.02em] max-w-5xl"
                        data-testid="build-detail-title"
                    >
                        {build.fullName || build.name}
                    </h1>

                    {isFull && (
                        <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 max-w-5xl">
                            {[
                                ["Engine", build.engine],
                                ["Power", build.horsepower],
                                ["Drivetrain", build.drivetrain],
                                ["Weight", build.weight],
                                ["0–100", build.zeroToSixty],
                            ].map(([label, value]) =>
                                value ? (
                                    <div
                                        key={label}
                                        className="bg-[var(--crewz-bg)]/35 backdrop-blur-md p-5 md:p-6 border border-white/5"
                                    >
                                        <p className="text-[10px] md:text-xs tracking-[0.22em] uppercase text-[var(--crewz-text)]/70 mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                                            {label}
                                        </p>
                                        <p className="font-display text-base md:text-lg leading-tight text-[var(--crewz-text)] drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]">
                                            {value}
                                        </p>
                                    </div>
                                ) : null
                            )}
                        </div>
                    )}
                </div>
            </section>

            {isFull ? <FullContent build={build} /> : <StubContent build={build} fallback={fallback} />}

            <Footer />
        </div>
    );
}

/* ----------------------------- FULL CONTENT ----------------------------- */

function FullContent({ build }) {
    return (
        <>
            {/* WHY IT'S AN ICON — light editorial section */}
            <section className="section-light" data-testid="build-icon-section">
                <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                    <div className="md:col-span-4">
                        <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-6">
                            Why It&apos;s An Icon
                        </p>
                        <h2 className="font-display text-4xl md:text-5xl text-[var(--crewz-text-dark)] leading-[0.95]">
                            The Story
                            <br />
                            Behind The Build.
                        </h2>
                    </div>
                    <div className="md:col-span-8">
                        <p className="text-lg md:text-xl leading-[1.7] text-[var(--crewz-text-dark)]/85">
                            {build.whyItsIcon}
                        </p>
                    </div>
                </div>
            </section>

            {/* IMAGE GALLERY (6 angles per flagship) */}
            {build.images && build.images.length > 1 && (
                <section
                    className="px-6 md:px-12 lg:px-20 py-20 md:py-28 max-w-7xl mx-auto"
                    data-testid="build-gallery-section"
                >
                    <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-4">
                        The Gallery
                    </p>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.95] tracking-[-0.01em] mb-12 max-w-3xl">
                        From Every Angle.
                    </h2>
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
                        {build.images.slice(1).map((entry, i) => {
                            // Backwards-compat: support string URL or {url, label}
                            const src = typeof entry === "string" ? entry : entry.url;
                            const label =
                                typeof entry === "string"
                                    ? ["Profile", "Rear Three-Quarter", "Front Detail", "Side View", "In The Wild"][i] || "Angle"
                                    : entry.label;
                            return (
                                <figure
                                    key={src}
                                    className="relative overflow-hidden bg-[var(--crewz-bg-3)] group mb-4 md:mb-6 break-inside-avoid"
                                    data-testid={`build-gallery-tile-${i}`}
                                >
                                    <img
                                        src={src}
                                        alt={`${build.name} — ${label}`}
                                        loading="lazy"
                                        className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                                        onError={(e) => {
                                            // Hide the entire tile if the image fails to load
                                            const fig = e.currentTarget.closest("figure");
                                            if (fig) fig.style.display = "none";
                                        }}
                                    />
                                    <figcaption className="px-4 py-3 bg-[var(--crewz-bg-2)] text-[10px] tracking-[0.22em] uppercase text-[var(--crewz-text-muted)] text-center border-t border-white/5">
                                        {label}
                                    </figcaption>
                                </figure>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* COMMON MODIFICATIONS */}
            {build.commonMods?.length > 0 && (
                <section
                    className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-7xl mx-auto border-t border-white/5"
                    data-testid="build-mods-section"
                >
                    <div className="flex items-center gap-3 mb-4">
                        <Wrench size={16} className="text-[var(--crewz-gold)]" strokeWidth={1.5} />
                        <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)]">
                            The Build Recipe
                        </p>
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.01em] mb-4 max-w-3xl">
                        Common Modifications.
                    </h2>
                    <p className="text-sm md:text-base text-[var(--crewz-text-muted)] mb-14 md:mb-20 max-w-3xl leading-relaxed">
                        <em>Compare prices — these are trusted retailers known for competitive pricing on the listed parts, not the only option. Lifetime warranties (FCP Euro), volume pricing (ECS Tuning, Tire Rack), and race-specialist retailers (Turner, BimmerWorld) typically beat manufacturer-direct MSRP.</em>
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
                        {build.commonMods.map((group, gi) => {
                            const isLastOdd =
                                gi === build.commonMods.length - 1 &&
                                build.commonMods.length % 2 === 1;
                            return (
                                <div
                                    key={group.area}
                                    className={`bg-[var(--crewz-bg)] p-7 md:p-10 ${
                                        isLastOdd ? "md:col-span-2" : ""
                                    }`}
                                >
                                    <div className={isLastOdd ? "md:max-w-2xl md:mx-auto" : ""}>
                                        <p className="font-display text-2xl md:text-3xl mb-7 tracking-[-0.01em] flex items-baseline gap-3">
                                            {group.area}
                                            <span className="text-xs tracking-[0.22em] uppercase text-[var(--crewz-text-muted)] font-body">
                                                {group.items.length} Upgrade{group.items.length === 1 ? "" : "s"}
                                            </span>
                                        </p>
                                        <ul className="space-y-7">
                                            {group.items.map((item) => {
                                                // Backwards-compat: support string OR {part, vendors}
                                                const part = typeof item === "string" ? item : item.part;
                                                const vendors = typeof item === "string" ? [] : item.vendors || [];
                                                return (
                                                    <li key={part} className="border-l-2 border-[var(--crewz-gold)] pl-5">
                                                        <p className="text-base md:text-lg leading-snug text-[var(--crewz-text)] mb-3">
                                                            {part}
                                                        </p>
                                                        {vendors.length > 0 && (
                                                            <div>
                                                                <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--crewz-text-muted)] mb-2">
                                                                    Top Suppliers
                                                                </p>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {vendors.map((v) => (
                                                                        <a
                                                                            key={v.name}
                                                                            href={v.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            data-testid={`vendor-link-${part.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40).replace(/-+$/, "")}-${v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24).replace(/-+$/, "")}`}
                                                                            className="group/v inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-[var(--crewz-gold)] hover:text-[var(--crewz-gold)] text-xs tracking-[0.06em] text-[var(--crewz-text)]/85 transition-colors"
                                                                        >
                                                                            {v.name}
                                                                            <ExternalLink size={10} className="opacity-60 group-hover/v:opacity-100" strokeWidth={1.6} />
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* INTERIOR */}
                    {build.interiorMods?.length > 0 && (
                        <div className="mt-16 md:mt-20 max-w-4xl">
                            <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-text-muted)] mb-6">
                                Inside The Cockpit
                            </p>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                                {build.interiorMods.map((mod) => (
                                    <li
                                        key={mod}
                                        className="flex items-start gap-3 text-base leading-relaxed text-[var(--crewz-text)]/85 py-2 border-b border-white/5"
                                    >
                                        <span className="mt-2 inline-block w-1 h-1 rotate-45 bg-[var(--crewz-gold)] shrink-0" />
                                        <span>{mod}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            )}

            {/* KNOWN ISSUES — sand light section */}
            {build.knownIssues?.length > 0 && (
                <section className="bg-[var(--crewz-sand)] text-[var(--crewz-text-dark)]" data-testid="build-issues-section">
                    <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-7xl mx-auto">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle size={16} className="text-[var(--crewz-gold)]" strokeWidth={1.6} />
                            <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)]">
                                Known Issues
                            </p>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.01em] max-w-3xl">
                            What To Watch For.
                        </h2>
                        <p className="mt-6 text-sm md:text-base text-[var(--crewz-text-dark)]/65 max-w-3xl leading-relaxed">
                            <em>
                                This information is curated from owner forums and community knowledge as a starting point.
                                It is not a substitute for a workshop manual or a qualified mechanic. CrewZNatioN accepts
                                no liability for work performed based on this content.
                            </em>
                        </p>

                        <div className="mt-14 md:mt-20 space-y-px bg-[var(--crewz-text-dark)]/10">
                            {build.knownIssues.map((issue) => (
                                <div
                                    key={issue.title}
                                    className="bg-[var(--crewz-sand)] py-8 md:py-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start"
                                >
                                    <div className="md:col-span-4">
                                        <p className="font-display text-2xl md:text-3xl leading-tight tracking-[-0.01em]">
                                            {issue.title}
                                        </p>
                                    </div>
                                    <div className="md:col-span-8 space-y-3">
                                        <p className="text-base md:text-lg leading-relaxed text-[var(--crewz-text-dark)]/85">
                                            {issue.summary}
                                        </p>
                                        <p className="text-sm md:text-base leading-relaxed text-[var(--crewz-text-dark)] border-l-2 border-[var(--crewz-gold)] pl-4">
                                            <span className="font-bold text-[var(--crewz-gold)] mr-1">Fix:</span>
                                            {issue.fix}
                                        </p>
                                        {issue.videoUrl && (
                                            <a
                                                href={issue.videoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`issue-video-${issue.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                                                className="group/yt inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold text-[var(--crewz-text-dark)] hover:text-[var(--crewz-gold)] transition-colors pt-1"
                                            >
                                                <Youtube size={16} strokeWidth={1.5} className="text-[var(--crewz-gold)]" />
                                                Watch The Fix On YouTube
                                                <span className="transition-transform group-hover/yt:translate-x-0.5">→</span>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* RESOURCES */}
            {build.resources && (
                <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-7xl mx-auto" data-testid="build-resources-section">
                    <div className="flex items-center gap-3 mb-4">
                        <BookOpen size={16} className="text-[var(--crewz-gold)]" strokeWidth={1.5} />
                        <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)]">
                            Reference Library
                        </p>
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.01em] mb-14 md:mb-20 max-w-3xl">
                        Where The Pros Go.
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
                        <ResourceColumn title="Forums" links={build.resources.forums} />
                        <ResourceColumn title="YouTube" links={build.resources.youtube} />
                        <ResourceColumn title="Parts &amp; Retailers" links={build.resources.partsRetailers} />
                    </div>
                </section>
            )}

            {/* CTA Footer band */}
            <CTABand />
        </>
    );
}

function ResourceColumn({ title, links }) {
    if (!links?.length) return null;
    return (
        <div>
            <p className="font-display text-2xl md:text-3xl mb-6 tracking-[-0.01em]">{title}</p>
            <ul className="space-y-4">
                {links.map((link) => (
                    <li key={link.url}>
                        <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-start gap-2 text-base text-[var(--crewz-text)]/85 hover:text-[var(--crewz-gold)] transition-colors leading-snug"
                        >
                            <span className="border-b border-transparent group-hover:border-[var(--crewz-gold)] transition-colors">
                                {link.label}
                            </span>
                            <ExternalLink size={12} className="mt-1 opacity-60 shrink-0" strokeWidth={1.6} />
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ----------------------------- STUB CONTENT ----------------------------- */

function StubContent({ build, fallback }) {
    return (
        <>
            <section className="section-light" data-testid="build-stub-section">
                <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-5xl mx-auto">
                    <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-6">
                        Full Write-Up In Progress
                    </p>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[var(--crewz-text-dark)] leading-[0.95] mb-8">
                        The {build.name} Editorial Is Being Built.
                    </h2>
                    <p className="text-base md:text-lg leading-[1.75] text-[var(--crewz-text-dark)]/80 max-w-3xl">
                        Our build editors are gathering the specs, modification playbooks, known
                        issues and parts catalogue for the {build.fullName || build.name}. In the
                        meantime, here are the most trusted starting points the wider community
                        recommends for this machine.
                    </p>
                </div>
            </section>

            {fallback && (
                <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-5xl mx-auto" data-testid="build-stub-fallback">
                    <div className="flex items-center gap-3 mb-4">
                        <BookOpen size={16} className="text-[var(--crewz-gold)]" strokeWidth={1.5} />
                        <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)]">
                            Community Starting Points · {build.manufacturer}
                        </p>
                    </div>
                    <h3 className="font-display text-3xl md:text-4xl leading-[0.95] tracking-[-0.01em] mb-12">
                        Where To Start Your Research.
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <a
                            href={fallback.forum}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block p-7 md:p-9 border border-white/10 hover:border-[var(--crewz-gold)] transition-colors"
                            data-testid="build-stub-forum-link"
                        >
                            <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--crewz-text-muted)] mb-3">
                                Reference Forum
                            </p>
                            <p className="font-display text-2xl md:text-3xl text-[var(--crewz-text)] group-hover:text-[var(--crewz-gold)] transition-colors flex items-center gap-3">
                                Owner Community
                                <ArrowUpRight size={20} strokeWidth={1.5} />
                            </p>
                        </a>
                        <a
                            href={fallback.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block p-7 md:p-9 border border-white/10 hover:border-[var(--crewz-gold)] transition-colors"
                            data-testid="build-stub-youtube-link"
                        >
                            <p className="text-[10px] tracking-[0.3em] uppercase text-[var(--crewz-text-muted)] mb-3">
                                Curated Videos
                            </p>
                            <p className="font-display text-2xl md:text-3xl text-[var(--crewz-text)] group-hover:text-[var(--crewz-gold)] transition-colors flex items-center gap-3">
                                YouTube Builds
                                <ArrowUpRight size={20} strokeWidth={1.5} />
                            </p>
                        </a>
                    </div>
                </section>
            )}

            <CTABand />
        </>
    );
}

/* ----------------------------- CTA BAND ----------------------------- */

function CTABand() {
    return (
        <section className="border-t border-white/5">
            <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-6xl mx-auto text-center">
                <p className="text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-6">
                    Drive Yours With Us
                </p>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.01em] mb-10 max-w-4xl mx-auto">
                    Bring The Build To <EventName />.
                </h3>
                <div className="flex flex-wrap justify-center gap-3">
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] px-7 py-4 text-sm tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                        data-testid="build-cta-membership"
                    >
                        Apply For Membership
                        <ArrowUpRight size={14} />
                    </Link>
                    <Link
                        to="/gathering"
                        className="inline-flex items-center gap-2 border border-white/20 hover:border-[var(--crewz-gold)] hover:text-[var(--crewz-gold)] text-[var(--crewz-text)] px-7 py-4 text-sm tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                        data-testid="build-cta-gathering"
                    >
                        Explore <EventName />
                        <ArrowUpRight size={14} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
